import CircularProgress from "@mui/material/CircularProgress";
import debounce from "lodash-es/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useGetPaymentMethods from "../../../apis/useGetPaymentMethods/useGetPaymentMethods";
import useSubmitCheckout, {
	CHECKOUT_ERROR_TYPES,
	ICheckoutError,
} from "../../../apis/useSubmitCheckout/useSubmitCheckout";
import ArrowRight from "../../../assets/images/ArrowRight.svg";
import { calculateTotalBcrsDeposit } from "../../../common/helpers/bcrsHelper";
import { isCartItemLimitExceeded } from "../../../common/helpers/itemOrderLimitHelper";
import { displayCentsWithDollarSign } from "../../../common/helpers/stringUtils";
import { useItemSubtotal } from "../../../common/hooks/useItemSubtotal";
import Condition from "../../../components/Conditional/Condition";
import { Image } from "../../../components/elements";
import PopupDialog from "../../../components/elements/PopupDialog/PopupDialog";
import { useCartContext } from "../../../contexts/CartServiceContext/CartServiceContext";
import { useConfigContext } from "../../../contexts/ConfigContext/ConfigContext";
import { APP_MODE } from "../../../contexts/ConfigContext/types";
import {
	usePaymentActions,
	usePaymentContext,
} from "../../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../../router/path";
import "./CheckoutButton.scss";
import "./CheckoutButton_flint.scss";
import "./CheckoutButton_kokoro.scss";
import "./CheckoutButton_pdd.scss";
import "./CheckoutButton_shabuDays.scss";
import "./CheckoutButton_torikizoku.scss";
import "./CheckoutButton_wang.scss";

export const CheckoutButton: React.FC = () => {
	const { t } = useTranslation();
	const { state: appMode } = useConfigContext((state) => state.appMode);
	const {
		state: { cart, is_takeaway, takeaway_byob },
	} = useCartContext((state) => state);
	const { state: order_level_takeaway_fee } = useConfigContext(
		(state) => state.config.takeaway_fee
	);
	const { state: bcrs_deposit_rate } = useConfigContext((state) => state.config.bcrs_deposit_rate);
	const { state: itemLimitPerPax } = useConfigContext((state) => state.config.item_limit_per_pax);
	const { state: pax } = useConfigContext((state) => state.qrSession?.pax);
	const { state: adyenMerchantName } = useConfigContext(
		(state) => state.config.adyen_merchant_name
	);
	const paymentActions = usePaymentActions();
	const { state: paymentReference } = usePaymentContext((state) => state.paymentReference);
	const navigate = useNavigate();
	const { getPaymentMethods, isLoading: isFetchingPaymentMethod } = useGetPaymentMethods();

	const {
		submitCheckout,
		isLoading: isSubmittingCheckout,
		error,
		removeOosItems,
		oosItemNames,
	} = useSubmitCheckout();
	const [showErrorPopup, setShowErrorPopup] = useState(false);
	const [errorAttemptCount, setErrorAttemptCount] = useState(0);
	const [showItemLimitPopup, setShowItemLimitPopup] = useState(false);
	const subtotal = useItemSubtotal(cart);
	const takeawayfee = is_takeaway && !takeaway_byob ? order_level_takeaway_fee : 0;

	// Calculate total BCRS deposit amount using helper
	const bcrsDepositAmount = useMemo(() => {
		return calculateTotalBcrsDeposit(cart, bcrs_deposit_rate);
	}, [cart, bcrs_deposit_rate]);
	const isDisabled =
		cart.length === 0 ||
		isSubmittingCheckout ||
		isFetchingPaymentMethod ||
		appMode === APP_MODE.VIEW;

	const debouncedSubmit = useMemo(
		() =>
			debounce(async () => {
				submitCheckout();
				if (adyenMerchantName != null) {
					await getPaymentMethods();
				}
			}, 300),
		[submitCheckout, getPaymentMethods, adyenMerchantName]
	);

	const handleCloseItemLimitPopup = useCallback(() => setShowItemLimitPopup(false), []);

	const itemLimitPopupButtons = useMemo(
		() => [{ text: t("itemLimit.exceeded.confirm"), onClick: handleCloseItemLimitPopup }],
		[t, handleCloseItemLimitPopup]
	);

	const handleClickCheckout = useCallback(() => {
		if (isCartItemLimitExceeded(cart, itemLimitPerPax, pax ?? 0)) {
			setShowItemLimitPopup(true);

			return;
		}

		setShowErrorPopup(false);
		debouncedSubmit();
	}, [debouncedSubmit, cart, itemLimitPerPax, pax]);

	const handleTryAgain = useCallback(() => {
		setShowErrorPopup(false);
		debouncedSubmit();
	}, [debouncedSubmit]);

	const handleCloseError = useCallback(() => {
		setShowErrorPopup(false);
		setErrorAttemptCount(0);
	}, []);

	useEffect(() => {
		paymentActions.CLEAR_PAYMENT_DATA();
	}, []);

	/**
	 * Only a reference that appears while this button is mounted - the checkout the
	 * customer just submitted - sends them to payment. One already present at mount is a
	 * leftover from an earlier order that the clear above is about to wipe; this effect's
	 * first run still sees it in its closure, and acting on it flashed the payment page
	 * on the way into the cart.
	 */
	const [mountReference] = useState(paymentReference);

	useEffect(() => {
		if (
			paymentReference &&
			paymentReference.length > 0 &&
			paymentReference !== mountReference &&
			isSubmittingCheckout === false &&
			isFetchingPaymentMethod === false
		) {
			navigate(PATH.PAYMENT);
		}
	}, [paymentReference, mountReference, navigate, isSubmittingCheckout, isFetchingPaymentMethod]);

	const handleRemoveOosItems = useCallback(() => {
		setShowErrorPopup(false);
		removeOosItems();
	}, [removeOosItems]);

	useEffect(() => {
		if (error) {
			setErrorAttemptCount((prev) => prev + 1);
			setShowErrorPopup(true);
		}
	}, [error]);

	// Type guard to check if error is ICheckoutError
	const isCheckoutError = (err: unknown): err is ICheckoutError => {
		return err !== null && typeof err === "object" && "type" in err && "items" in err;
	};

	// Memoize popup content based on error type
	const isOosError = isCheckoutError(error) && error.type === CHECKOUT_ERROR_TYPES.OOS_ERROR;

	const popupTitle = useMemo(() => {
		if (isOosError) {
			return t("cartPage.oosTitle");
		}

		return errorAttemptCount === 1
			? t("paymentPage.linkpay.timeoutTitle")
			: t("paymentPage.linkpay.failedTitle");
	}, [isOosError, errorAttemptCount, t]);

	const popupMessage = useMemo(() => {
		if (isOosError && oosItemNames.length > 0) {
			return (
				<>
					<div className="OosMessage">{t("cartPage.oosMessage")}</div>
					<div className="OosItemsList">
						{oosItemNames.map((name, index) => (
							<div key={index + name} className="OosItem">
								{name}
							</div>
						))}
					</div>
				</>
			);
		}

		return errorAttemptCount === 1
			? t("paymentPage.linkpay.timeoutMessage")
			: t("paymentPage.linkpay.failedMessage");
	}, [isOosError, errorAttemptCount, t, oosItemNames]);

	const popupButtons = useMemo(() => {
		if (isOosError) {
			return [
				{
					text: t("cartPage.oosRemove"),
					onClick: handleRemoveOosItems,
					className: "PrimaryButton",
				},
			];
		}

		if (errorAttemptCount === 1) {
			return [
				{
					text: t("paymentPage.linkpay.tryAgain"),
					onClick: handleTryAgain,
					className: "PrimaryButton",
				},
			];
		}

		return [
			{
				text: t("paymentPage.linkpay.close"),
				onClick: handleCloseError,
				className: "PrimaryButton",
			},
		];
	}, [isOosError, errorAttemptCount, t, handleTryAgain, handleCloseError, handleRemoveOosItems]);

	return (
		<>
			<button
				className={`CheckoutButton ${isDisabled ? "Disabled" : ""}`}
				onClick={handleClickCheckout}
				disabled={isDisabled}
			>
				<div className="CheckoutInfoWrapper">
					<div className="Subtotal">
						{displayCentsWithDollarSign(subtotal + takeawayfee + bcrsDepositAmount)}
					</div>
				</div>
				<div className="CheckoutButtonLabel">
					<Condition check={!isSubmittingCheckout && !isFetchingPaymentMethod}>
						{t("cartPage.checkoutBtn")}
						<Image src={ArrowRight} alt="ArrowRight" className="ArrowRight" />
					</Condition>
					<Condition check={isSubmittingCheckout || isFetchingPaymentMethod}>
						<CircularProgress size={24} />
					</Condition>
				</div>
			</button>

			<PopupDialog
				data-testid="item-limit-exceeded-dialog"
				open={showItemLimitPopup}
				title={t("itemLimit.exceeded.title")}
				content={<span>{t("itemLimit.exceeded.message")}</span>}
				buttons={itemLimitPopupButtons}
				onClose={handleCloseItemLimitPopup}
			/>

			<PopupDialog
				data-testid="checkout-error-popup"
				open={showErrorPopup}
				title={popupTitle}
				onClose={handleCloseError}
				content={<div className="ErrorContent">{popupMessage}</div>}
				buttons={popupButtons}
				className="CheckoutErrorPopup"
			/>
		</>
	);
};
